import { useState, useEffect, useRef } from "react";
import { Search, Calculator, Copy, Check, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "";

const QuotationPage = () => {
  const token = localStorage.getItem("token");
  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dropdownRef = useRef(null);
  const [invoiceType, setInvoiceType] = useState("C");
  const [itemType, setItemType] = useState("DOC");
  const [weight, setWeight] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/customers?limit=5000`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => setCustomers(Array.isArray(data) ? data : data.data || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = searchQuery.length >= 2
    ? customers.filter((c) => c.company?.toLowerCase().includes(searchQuery.toLowerCase()) || c.customer_id?.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10)
    : [];

  const getRateCode = (cust) => {
    if (!cust) return null;
    if (invoiceType === "C") return cust.rate_code;
    if (invoiceType === "K") return cust.rate_code_imp;
    if (invoiceType === "D") return cust.rate_code_dom;
    return null;
  };

  const handleSelectCustomer = (cust) => { setSelectedCustomer(cust); setSearchQuery(cust.company); setShowDropdown(false); setResult(null); setError(""); };

  const handleCalculate = async () => {
    if (!selectedCustomer) { setError("Pilih customer dulu"); return; }
    if (!weight || isNaN(weight) || parseFloat(weight) <= 0) { setError("Berat harus diisi"); return; }
    if (!country.trim()) { setError("Negara harus diisi"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const params = new URLSearchParams({ customer_id: selectedCustomer.id, weight, country_name: country, invoice_type: invoiceType, item_type: itemType });
      const res = await fetch(`${API}/api/sales/quotation?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal menghitung");
      setResult(data);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.text_output).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const rateCode = getRateCode(selectedCustomer);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Quotation</h1>
        <p className="text-sm text-slate-500 mt-1">Hitung estimasi harga pengiriman</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5">
        <div ref={dropdownRef} className="relative">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Customer</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); if (!e.target.value) setSelectedCustomer(null); }} onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)} placeholder="Cari nama PT atau customer ID..." className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" />
          </div>
          {showDropdown && filtered.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
              {filtered.map((c) => (
                <button key={c.id} onClick={() => handleSelectCustomer(c)} className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.company}</p>
                  <p className="text-xs text-slate-400">{c.customer_id}</p>
                </button>
              ))}
            </div>
          )}
          {selectedCustomer && rateCode && <p className="text-xs text-blue-600 mt-1.5 font-medium">Rate Code: <span className="font-mono bg-blue-50 px-1.5 py-0.5 rounded">{rateCode}</span></p>}
          {selectedCustomer && !rateCode && <p className="text-xs text-red-500 mt-1.5">⚠ Customer tidak punya rate code untuk tipe {invoiceType}</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tipe Invoice</label>
            <div className="relative">
              <select value={invoiceType} onChange={(e) => { setInvoiceType(e.target.value); setResult(null); }} className="w-full appearance-none px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400">
                <option value="C">Export (C)</option>
                <option value="K">Import (K)</option>
                <option value="D">Domestic (D)</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Jenis Kiriman</label>
            <div className="flex gap-2">
              {["DOC", "PARCEL"].map((t) => (
                <button key={t} onClick={() => { setItemType(t); setResult(null); }} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${itemType === t ? "bg-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                  {t === "DOC" ? "Document" : "Non-Doc"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Berat (kg)</label>
            <input type="number" value={weight} onChange={(e) => { setWeight(e.target.value); setResult(null); }} placeholder="0.5" step="0.5" min="0" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Negara Tujuan</label>
            <input type="text" value={country} onChange={(e) => { setCountry(e.target.value); setResult(null); }} placeholder="JAPAN" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" />
          </div>
        </div>
        {error && <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>}
        <button onClick={handleCalculate} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold rounded-xl transition-colors shadow-md">
          <Calculator className="w-4 h-4" />
          {loading ? "Menghitung..." : "Hitung Sekarang"}
        </button>
      </div>
      {result && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">{result.company}</p>
              <p className="text-sm font-semibold text-slate-700 mt-0.5">{result.type_label} · Zone {result.zone} · {result.weight_calculated} kg</p>
            </div>
            <button onClick={handleCopy} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${copied ? "bg-green-500 text-white border-green-500" : "bg-slate-800 text-white border-slate-800 hover:bg-black"}`}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Tersalin!" : "Copy"}
            </button>
          </div>
          <pre className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 text-sm font-mono text-slate-800 whitespace-pre-wrap leading-relaxed">{result.text_output}</pre>
          {result.fsc_exempt && <p className="text-xs text-green-600 font-medium">✓ Customer ini bebas FSC</p>}
        </div>
      )}
    </div>
  );
};

export default QuotationPage;
